import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { HiOutlineSearch } from "react-icons/hi";
import { IoShareOutline } from "react-icons/io5";
import { MdClear, MdOutlineEvent } from "react-icons/md";
import Button from "../Utilities/Button";
import Modal from "../Utilities/Modal";
import PaginationNew from "../Utilities/PaginationNew";
import RightPanelLayout from "../Layout/RightPanelLayout";
import styles from "./style/RegistrationList.module.scss";
import {
  exportCenterRegistrationsCsv,
  fetchRegistrationsByEmailActivities,
  fetchRegistrationsList,
} from "./activitiesApi";
import {
  mapRegistrationByEmailActivityRow,
  mapRegistrationApiRowToList,
  normalizeRegistrationListResponse,
} from "./activityTransforms";

const LIMIT = 10;

const formatDateTime = (value) => {
  if (!value) {
    return "—";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "—";
  }
  return `${format(parsed, "yyyy-MM-dd")} ${format(parsed, "HH:mm")}`;
};

const formatActivityDatetimeCell = (value) => {
  if (!value) {
    return { date: "—", timeRange: "" };
  }
  const raw = String(value).trim();
  if (!raw) {
    return { date: "—", timeRange: "" };
  }

  const normalized = raw
    .replace(/\//g, "-")
    .replace(/（[^）]*）/g, "")
    .replace(/\([^)]*\)/g, "")
    .replace(/\s+/g, " ")
    .trim();

  const matched = normalized.match(
    /^(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2}\s*-\s*\d{2}:\d{2})$/,
  );
  if (matched) {
    return {
      date: matched[1],
      timeRange: matched[2].replace(/\s+/g, ""),
    };
  }

  return { date: normalized, timeRange: "" };
};

const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

const RegistrationList = () => {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchApplied, setSearchApplied] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(true);
  const [openedRegistrant, setOpenedRegistrant] = useState(null);
  const [modalEvents, setModalEvents] = useState([]);
  const [eventLoading, setEventLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const applyListSearch = () => {
    setSearchApplied(searchInput.trim());
    setPage(1);
  };

  useEffect(() => {
    let cancelled = false;
    setListLoading(true);
    fetchRegistrationsList({
      search: searchApplied,
      page,
      limit: LIMIT,
    })
      .then((raw) => {
        if (cancelled) {
          return;
        }
        const normalized = normalizeRegistrationListResponse(raw);
        setRows((normalized.rows ?? []).map(mapRegistrationApiRowToList));
        setTotal(Number(normalized.total) || 0);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setRows([]);
        setTotal(0);
      })
      .finally(() => {
        if (!cancelled) {
          setListLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [page, searchApplied]);

  useEffect(() => {
    if (!openedRegistrant?.email) {
      setModalEvents([]);
      return;
    }
    let cancelled = false;
    const loadRegistrantEvents = async () => {
      setEventLoading(true);
      try {
        const allRows = [];
        let currentPage = 1;
        let expectedTotal = 0;
        while (true) {
          const raw = await fetchRegistrationsByEmailActivities({
            email: openedRegistrant.email,
            page: currentPage,
            limit: 200,
          });
          const normalized = normalizeRegistrationListResponse(raw);
          const pageRows = (normalized.rows ?? []).map(
            mapRegistrationByEmailActivityRow,
          );
          allRows.push(...pageRows);
          expectedTotal = Number(normalized.total) || allRows.length;
          if (allRows.length >= expectedTotal || pageRows.length === 0) {
            break;
          }
          currentPage += 1;
        }
        if (!cancelled) {
          setModalEvents(allRows);
        }
      } catch {
        if (!cancelled) {
          setModalEvents([]);
        }
      } finally {
        if (!cancelled) {
          setEventLoading(false);
        }
      }
    };
    loadRegistrantEvents();
    return () => {
      cancelled = true;
    };
  }, [openedRegistrant]);

  const onPageChange = (selectedObj) => {
    setPage(selectedObj.selected + 1);
  };

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / LIMIT)),
    [total],
  );

  const handleExport = async () => {
    if (exporting || total === 0) {
      return;
    }
    setExporting(true);
    try {
      const res = await exportCenterRegistrationsCsv({
        search: searchApplied,
      });
      const disposition = res?.headers?.["content-disposition"] ?? "";
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `報名人員列表_${format(
        new Date(),
        "yyyyMMdd_HHmmss",
      )}.csv`;
      downloadBlob(
        res.data,
        (filenameMatch && filenameMatch[1]) || fallbackName,
      );
    } finally {
      setExporting(false);
    }
  };

  return (
    <RightPanelLayout title="活動管理">
      <div className={styles.listToolbar}>
        <div className={styles.listToolbarTitle}>報名人員列表</div>
        <div className={styles.toolbarActions}>
          <div className={styles.searchBar}>
            <button
              type="button"
              className={styles.searchBarIconBtn}
              onClick={applyListSearch}
              aria-label="搜尋"
            >
              <HiOutlineSearch className={styles.searchBarIcon} />
            </button>
            <input
              type="search"
              className={styles.searchBarInput}
              value={searchInput}
              placeholder="搜尋關鍵字"
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  applyListSearch();
                }
              }}
              aria-label="搜尋報名資料"
            />
            {searchInput ? (
              <button
                type="button"
                className={styles.searchBarClear}
                onClick={() => {
                  setSearchInput("");
                  setSearchApplied("");
                  setPage(1);
                }}
                aria-label="清除搜尋"
              >
                <MdClear className={styles.searchBarClearIcon} />
              </button>
            ) : null}
          </div>
          <Button type="primary" title="搜尋" onClick={applyListSearch} />
          <Button
            type="primary"
            title={exporting ? "匯出中…" : "匯出"}
            icon={<IoShareOutline fontSize={18} />}
            onClick={handleExport}
            disabled={listLoading || total === 0 || exporting}
          />
        </div>
      </div>

      {!listLoading && total > 0 && (
        <div className={styles.tablePaginationTop}>
          <PaginationNew
            onPageChange={onPageChange}
            pageCount={page}
            limit={LIMIT}
            totalPages={totalPages}
            totalData={total}
          />
        </div>
      )}

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>姓名</th>
              <th>電話</th>
              <th>E-mail</th>
              <th>公司</th>
              <th>職位</th>
              <th>報名活動列表</th>
            </tr>
          </thead>
          <tbody>
            {listLoading ? (
              <tr>
                <td colSpan={6} className={styles.tableEmptyCell}>
                  載入中…
                </td>
              </tr>
            ) : null}
            {!listLoading && rows.length > 0
              ? rows.map((registration) => {
                return (
                  <tr key={registration.id}>
                    <td>{registration.name}</td>
                    <td className={styles.latinCell}>{registration.phone}</td>
                    <td className={styles.latinCell}>{registration.email}</td>
                    <td>{registration.companyName}</td>
                    <td>{registration.jobTitle}</td>
                    <td>
                      <button
                        type="button"
                        className={styles.linkButton}
                        aria-label="查看活動資訊"
                        onClick={() =>
                          setOpenedRegistrant({
                            email: registration.email,
                            name: registration.name,
                          })
                        }
                      >
                        <MdOutlineEvent />
                      </button>
                    </td>
                  </tr>
                );
              })
              : null}
            {!listLoading && rows.length === 0 ? (
              <tr>
                <td colSpan={6} className={styles.tableEmptyCell}>
                  目前沒有符合條件的報名資料。
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={Boolean(openedRegistrant)}
        size="xl"
        width="min(960px, calc(100vw - 32px))"
      >
        <div className={styles.eventModalRoot}>
          <header className={styles.eventModalTop}>
            <h2 className={styles.eventModalTitle}>活動資訊</h2>
          </header>
          <div className={styles.eventModalBody}>
            {eventLoading ? (
              <div className={styles.eventModalEmpty}>載入中…</div>
            ) : modalEvents.length > 0 ? (
              <table className={styles.modalTable}>
                <thead>
                  <tr>
                    <th>報名時間</th>
                    <th>姓名</th>
                    <th>活動名稱</th>
                    <th>活動時間</th>
                    <th>活動地點</th>
                    <th>備註</th>
                  </tr>
                </thead>
                <tbody>
                  {modalEvents.map((eventItem) => (
                    <tr key={eventItem.id}>
                      <td className={styles.latinCell}>
                        {formatDateTime(eventItem.submittedAt)}
                      </td>
                      <td>{eventItem.name || "—"}</td>
                      <td>{eventItem.activityTitle || "—"}</td>
                      <td className={styles.latinCell}>
                        {(() => {
                          const activityTime = formatActivityDatetimeCell(
                            eventItem.activityDatetime,
                          );
                          return activityTime.timeRange ? (
                            <>
                              <div>{activityTime.date}</div>
                              <div>{activityTime.timeRange}</div>
                            </>
                          ) : (
                            activityTime.date
                          );
                        })()}
                      </td>
                      <td>{eventItem.activityLocation || "—"}</td>
                      <td>{eventItem.remark?.trim() || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className={styles.eventModalEmpty}>
                找不到該用戶的活動資訊。
              </div>
            )}
          </div>
          <footer className={styles.eventModalFooter}>
            <button
              type="button"
              className={styles.eventModalCloseBtn}
              onClick={() => setOpenedRegistrant(null)}
            >
              關閉
            </button>
          </footer>
        </div>
      </Modal>
    </RightPanelLayout>
  );
};

export default RegistrationList;
